import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StorageService, UserService, LanguageService } from '../services';
import { AUTH_ROUTES } from '../tokens';
import { map, catchError, timeout } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Blocks access to protected pages until the user has completed the questionnaire.
 * Fetches the user profile from the API and checks `profile.termsAccepted`.
 */
export const questionnaireGuard: CanActivateFn = () => {
  const router = inject(Router);
  const storage = inject(StorageService);
  const userService = inject(UserService);
  const routes = inject(AUTH_ROUTES);

  if (!routes.questionnaireUrl) {
    console.log('[questionnaireGuard] → pass (no questionnaireUrl configured)');
    return true;
  }

  const questionnaireTree = router.parseUrl(routes.questionnaireUrl);

  const userId = storage.getItem('userId');
  const cached = storage.getItem('questionnaireCompleted');
  console.log('[questionnaireGuard]', { userId, cached });

  if (!userId) {
    console.log('[questionnaireGuard] → redirect (no userId)');
    return questionnaireTree;
  }

  if (cached === 'true') {
    console.log('[questionnaireGuard] → pass (cached)');
    return true;
  }

  console.log('[questionnaireGuard] → calling API...');
  return userService.get(userId).pipe(
    timeout(10000),
    map((user) => {
      const termsAccepted = user?.profile?.termsAccepted;
      console.log('[questionnaireGuard] API result:', { termsAccepted, profile: user?.profile });
      if (termsAccepted != true) {
        console.log('[questionnaireGuard] → redirect to questionnaire');
        return questionnaireTree;
      }
      storage.setItem('questionnaireCompleted', 'true');
      console.log('[questionnaireGuard] → pass (API confirmed)');
      return true;
    }),
    catchError((err) => {
      console.log('[questionnaireGuard] → redirect (API error)', err);
      return of(questionnaireTree);
    })
  );
};

/**
 * Prevents re-entry to the questionnaire page once the user has already completed it.
 * Fetches the user profile from the API and checks `profile.termsAccepted`.
 */
export const questionnaireCompletedGuard: CanActivateFn = () => {
  const router = inject(Router);
  const storage = inject(StorageService);
  const userService = inject(UserService);
  const langService = inject(LanguageService);

  const userId = storage.getItem('userId');
  if (!userId) {
    return true;
  }

  return userService.get(userId).pipe(
    timeout(10000),
    map((user) => {
      if (user?.profile?.termsAccepted === true) {
        storage.setItem('questionnaireCompleted', 'true');
        return router.createUrlTree(['/', langService.currentLang || 'en']);
      }
      return true;
    }),
    catchError(() => of(true))
  );
};
